import type { AudioItem } from '../items/audio/audio-item-type';
import { isAudioItem } from '../items/audio/audio-item-type';
import type { EditorStarterItem } from '../items/item-type';
import type { TrackType } from '../state/types';

export interface ContinuousAudioGroup {
  trackId: string;
  assetId: string;
  itemIds: string[];
  fromFrame: number;
  toFrame: number;
  trimBeforeFrames: number;
  decibelAdjustment: number;
  playbackRate: number;
  trackMuted: boolean;
}

interface BuildContinuousAudioGroupsParams {
  tracks: TrackType[];
  items: Record<string, EditorStarterItem>;
  fps: number;
}

const canJoinContinuousAudio = (
  previous: AudioItem,
  current: AudioItem,
  fps: number,
): boolean => {
  if (
    previous.assetId !== current.assetId ||
    previous.from + previous.durationInFrames !== current.from ||
    previous.playbackRate !== current.playbackRate ||
    previous.decibelAdjustment !== current.decibelAdjustment
  ) {
    return false;
  }

  if (
    previous.audioFadeInDurationInSeconds !== 0 ||
    previous.audioFadeOutDurationInSeconds !== 0 ||
    current.audioFadeInDurationInSeconds !== 0 ||
    current.audioFadeOutDurationInSeconds !== 0
  ) {
    return false;
  }

  const previousSourceEnd =
    previous.audioStartFromInSeconds +
    (previous.durationInFrames / fps) * previous.playbackRate;
  const sourceToleranceInSeconds = 1 / (fps * 1000);

  return (
    Math.abs(current.audioStartFromInSeconds - previousSourceEnd) <=
    sourceToleranceInSeconds
  );
};

const isEligibleAudioItem = (
  item: EditorStarterItem | undefined,
): item is AudioItem => isAudioItem(item) && !item.renderMeta?.vendorModel;

export const buildContinuousAudioGroups = ({
  tracks,
  items,
  fps,
}: BuildContinuousAudioGroupsParams): ContinuousAudioGroup[] => {
  const groups: ContinuousAudioGroup[] = [];

  for (const track of tracks) {
    const audioItems = track.items
      .map((itemId) => items[itemId])
      .filter(isAudioItem)
      .sort((a, b) => a.from - b.from);
    let currentGroup: AudioItem[] = [];

    const flushGroup = () => {
      if (currentGroup.length < 2) {
        currentGroup = [];
        return;
      }

      const first = currentGroup[0]!;
      const last = currentGroup[currentGroup.length - 1]!;
      groups.push({
        trackId: track.id,
        assetId: first.assetId,
        itemIds: currentGroup.map((item) => item.id),
        fromFrame: first.from,
        toFrame: last.from + last.durationInFrames,
        trimBeforeFrames: first.audioStartFromInSeconds * fps,
        decibelAdjustment: first.decibelAdjustment,
        playbackRate: first.playbackRate,
        trackMuted: track.muted,
      });
      currentGroup = [];
    };

    for (const item of audioItems) {
      if (!isEligibleAudioItem(item)) {
        flushGroup();
        continue;
      }

      const previous = currentGroup[currentGroup.length - 1];
      if (!previous) {
        currentGroup = [item];
        continue;
      }

      if (canJoinContinuousAudio(previous, item, fps)) {
        currentGroup.push(item);
        continue;
      }

      flushGroup();
      currentGroup = [item];
    }

    flushGroup();
  }

  return groups;
};
